import React from 'react';
import { Box, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuth } from '../../Context/AuthContext';
import { useGetQuizAssignmentsQuery, useAcceptQuizAssignmentMutation } from '../../services/SkillsManagement';
import QuizCard from '../../SystemDesign/QuizCard';
import DeclineAssignment from './DeclineAssignment';

/**
 * AssignedQuizzesView
 * Renders a grid of quizzes assigned by a manager for the current user.
 */
const AssignedQuizzesView = ({ open = true, onStart }) => {
  const { user } = useAuth();
  const [acceptAssignment] = useAcceptQuizAssignmentMutation();

  // Fetch only pending assignments for current user
  const { data: assignmentsData, isLoading } = useGetQuizAssignmentsQuery(
    Number.isFinite(Number(user?.id))
      ? { page: 1, limit: 20, assigneeUserId: Number(user?.id), status: 'pending' }
      : skipToken,
    { skip: !open || !Number.isFinite(Number(user?.id)), refetchOnMountOrArgChange: true }
  );

  const assignedQuizzes = React.useMemo(() => {
    const arr = assignmentsData?.data?.assignments || assignmentsData?.assignments || assignmentsData?.data || [];
    const list = Array.isArray(arr) ? arr : [];
    return list.map((a) => {
      const q = a?.quiz || a;
      return {
        assignmentId: a?.assignmentId ?? a?.id ?? a?.quizAssignmentId,
        quizId: Number(q?.quizId ?? q?.id),
        title: q?.title || a?.title,
        description: q?.description || a?.description,
        timeLimit: q?.timeLimit,
        maxAttempts: q?.maxAttempts,
        status: a?.status || 'assigned',
        updatedAt: a?.updatedAt || q?.updatedAt,
        dueAt: a?.dueAt,
        expiresAt: a?.expiresAt,
        assigner: a?.assigner || (a?.assignedByUsername ? { username: a.assignedByUsername } : undefined),
        assignedBy: a?.assignedBy || a?.assignedByUsername,
      };
    }).filter((x) => Number.isFinite(x.quizId));
  }, [assignmentsData]);

  // Local reducer-like state to reflect immediate UI changes (e.g., after decline)
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    setItems(assignedQuizzes);
  }, [assignedQuizzes]);

  return (
    <Box sx={{ width: '100%', maxWidth: 'min(1200px, calc(100vw - 32px))', mx: 'auto' }}>
      {/* <Typography variant="subtitle1" sx={{ mb: 1 }}>Quizzes assigned by your manager</Typography> */}
      {isLoading ? (
        <Typography variant="body2">Loading assigned quizzes...</Typography>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No assigned quizzes.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
          {items.map((as, idx) => {
            const quiz = as.quiz || as;
            const qid = Number(as.quizId || quiz?.quizId || quiz?.id);
            const quizForCard = {
              ...quiz,
              questionsCount: quiz?.questionsCount,
              skillsCount: quiz?.skillsCount,
            };
            const idForRemove = as.assignmentId || as.id;
            return (
              <QuizCard
                key={as.assignmentId || qid || idx}
                quiz={quizForCard}
                index={idx}
                assignment={as}
                onStart={async () => {
                  try {
                    const assignmentId = idForRemove;
                    if (assignmentId) {
                      await acceptAssignment({ assignmentId }).unwrap();
                    }
                  } catch {}
                  const qpId = Number(quiz?.posteId ?? quiz?.poste_id);
                  onStart?.({ qid, posteId: qpId });
                }}
                showUpdated={false}
                showCreator={false}
                assignmentActions={
                  <DeclineAssignment
                    assignmentId={idForRemove}
                    onDeclined={() => {
                      // Optimistically remove declined assignment from the view
                      setItems((prev) => prev.filter((x) => (x.assignmentId || x.id) !== idForRemove));
                    }}
                  />
                }
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AssignedQuizzesView;
